/**
 * Question Review — gate-by-gate walkthrough tool for Bryan + the team.
 *
 * Purpose: tomorrow morning's working session. Show every question, grouped by
 * 10 Checkpoints (CP1-CP10), with Keep / Edit / Remove buttons and an "add a question
 * here" box per gate. Decisions persist in localStorage so we can resume mid-walk;
 * "Download review" exports JSON we can apply to the question bank afterwards.
 *
 * Pre-flagging rules (locked May 5, 2026):
 *   - Suspicious: questions whose `cp` value is 0 or non-integer (off-gate)
 *   - Suspicious: very long labels (>140 chars) — usually run-on or compound
 *   - Suspicious: industry-trigger inconsistency — overlapping industry tags
 *     (per Bryan's "do NOT conflate" rule)
 *
 * NOT persisted to DB tonight on purpose — Bryan's "production-ready,
 * hundreds of thousands of users" rule means we don't ship live question-bank
 * mutations without a proper migration + audit trail. This page captures
 * decisions; tomorrow we apply them as a clean migration.
 */
import { useEffect, useMemo, useState } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { api } from "@/lib/session";
import { Link } from "wouter";

type Decision = "keep" | "edit" | "remove";

interface QuestionOption {
  key: string;
  label: string;
  flag?: string;
  points?: number;
}

interface QuestionDef {
  key: string;
  cp: number;
  label: string;
  helpText?: string;
  options: QuestionOption[];
  industryTrigger?: string | string[] | null;
  subVertical?: string | null;
}

interface ReviewEntry {
  decision: Decision;
  editedLabel?: string;
  editedHelpText?: string;
  note?: string;
  reviewedAt: string;
}

interface AddedQuestion {
  cp: number;
  label: string;
  helpText: string;
  addedAt: string;
}

interface ReviewState {
  decisions: Record<string, ReviewEntry>; // keyed by question.key
  added: AddedQuestion[];
  startedAt: string;
}

// Canonical CP gate names — locked 2026-05-26 per helix-checkpoints.ts
const GATE_NAMES: Record<number, string> = {
  1:  "Project",
  2:  "Need / Pain / Demonstration",
  3:  "Budget",
  4:  "Timeline",
  5:  "Authority — Project / Initiative",
  6:  "Technical and Financial Sign-off",
  7:  "Org Structure and Influence Map",
  8:  "Competitive Position",
  9:  "Compliance — IT Internal and Industry-Specific",
  10: "Legal — Contracts, SLAs, Terms",
};

const STORAGE_KEY = "aip:question-review:v1";

function emptyState(): ReviewState {
  return { decisions: {}, added: [], startedAt: new Date().toISOString() };
}

function loadState(): ReviewState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
  } catch {}
  return emptyState();
}

function saveState(state: ReviewState) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

function isSuspicious(q: QuestionDef): { flagged: boolean; reasons: string[] } {
  const reasons: string[] = [];
  if (!Number.isInteger(q.cp) || q.cp < 1 || q.cp > 9) {
    reasons.push(`Off-gate (cp=${q.cp})`);
  }
  if (q.label && q.label.length > 140) {
    reasons.push("Long label (>140 chars)");
  }
  if (q.label && /\b(and|or)\b.*\b(and|or)\b/i.test(q.label) && q.label.length > 80) {
    reasons.push("Compound question");
  }
  if (Array.isArray(q.industryTrigger) && q.industryTrigger.length > 1 && new Set(q.industryTrigger).size !== q.industryTrigger.length) {
    reasons.push("Duplicate industry triggers");
  }
  if (!q.options || q.options.length < 2) {
    reasons.push("Fewer than 2 options");
  }
  return { flagged: reasons.length > 0, reasons };
}

export default function QuestionReview() {
  const [questions, setQuestions] = useState<QuestionDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<ReviewState>(() => loadState());
  const [activeGate, setActiveGate] = useState<number>(1);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [showOnlySuspicious, setShowOnlySuspicious] = useState<boolean>(false);
  const [showOnlyUndecided, setShowOnlyUndecided] = useState<boolean>(false);
  const [newQuestionLabel, setNewQuestionLabel] = useState("");
  const [newQuestionHelp, setNewQuestionHelp] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api<{ questions: QuestionDef[] }>("/api/v3/catalog");
        if (cancelled) return;
        setQuestions(data.questions ?? []);
      } catch (e) {
        // silent — page still renders empty
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Persist on every state change
  useEffect(() => { saveState(state); }, [state]);

  const byGate = useMemo(() => {
    const map: Record<number, QuestionDef[]> = {};
    questions.forEach((q) => {
      if (!map[q.cp]) map[q.cp] = [];
      map[q.cp].push(q);
    });
    return map;
  }, [questions]);

  const gateCounts = useMemo(() => {
    const counts: Record<number, { total: number; reviewed: number; flagged: number }> = {};
    for (let cp = 1; cp <= 10; cp++) {
      const list = byGate[cp] ?? [];
      counts[cp] = {
        total: list.length,
        reviewed: list.filter((q) => state.decisions[q.key]).length,
        flagged: list.filter((q) => isSuspicious(q).flagged).length,
      };
    }
    return counts;
  }, [byGate, state.decisions]);

  const overall = useMemo(() => {
    const total = questions.length;
    const reviewed = questions.filter((q) => state.decisions[q.key]).length;
    return { total, reviewed, pct: total ? Math.round((reviewed / total) * 100) : 0 };
  }, [questions, state.decisions]);

  function setDecision(key: string, decision: Decision, extra?: Partial<ReviewEntry>) {
    setState((s) => ({
      ...s,
      decisions: {
        ...s.decisions,
        [key]: {
          ...(s.decisions[key] ?? {}),
          decision,
          reviewedAt: new Date().toISOString(),
          ...(extra ?? {}),
        },
      },
    }));
  }

  function clearDecision(key: string) {
    setState((s) => {
      const next = { ...s.decisions };
      delete next[key];
      return { ...s, decisions: next };
    });
  }

  function addQuestion() {
    if (!newQuestionLabel.trim()) return;
    setState((s) => ({
      ...s,
      added: [
        ...s.added,
        {
          cp: activeGate,
          label: newQuestionLabel.trim(),
          helpText: newQuestionHelp.trim(),
          addedAt: new Date().toISOString(),
        },
      ],
    }));
    setNewQuestionLabel("");
    setNewQuestionHelp("");
  }

  function removeAdded(idx: number) {
    setState((s) => ({ ...s, added: s.added.filter((_, i) => i !== idx) }));
  }

  function downloadReview() {
    const payload = {
      ...state,
      exportedAt: new Date().toISOString(),
      summary: {
        total_questions: questions.length,
        decisions_count: Object.keys(state.decisions).length,
        added_count: state.added.length,
        keep_count: Object.values(state.decisions).filter((d) => d.decision === "keep").length,
        edit_count: Object.values(state.decisions).filter((d) => d.decision === "edit").length,
        remove_count: Object.values(state.decisions).filter((d) => d.decision === "remove").length,
      },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aip-question-review-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function resetAll() {
    if (!confirm("Reset all review decisions? This cannot be undone.")) return;
    setState(emptyState());
  }

  const visibleQuestions = useMemo(() => {
    let list = byGate[activeGate] ?? [];
    if (showOnlySuspicious) list = list.filter((q) => isSuspicious(q).flagged);
    if (showOnlyUndecided) list = list.filter((q) => !state.decisions[q.key]);
    return list;
  }, [byGate, activeGate, showOnlySuspicious, showOnlyUndecided, state.decisions]);

  const addedForGate = state.added.filter((a) => a.cp === activeGate);

  return (
    <OperatorShell breadcrumbs={[{ label: "Question Review" }]} pageTitle="Question Review" pageMeta="Checkpoint walkthrough">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 12, color: "#6B7280", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
              Manager — Question Review
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 600, color: "#111827", margin: 0 }}>
              Walk the question bank, checkpoint by checkpoint
            </h1>
            <div style={{ fontSize: 14, color: "#6B7280", marginTop: 6, maxWidth: 760 }}>
              Keep, edit, or remove each question. Add new questions per checkpoint. Decisions save automatically to this device. Download as JSON when finished and we will apply them as a clean migration.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={downloadReview}
              style={{ fontSize: 13, padding: "8px 14px", background: "#111827", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
            >
              Download review
            </button>
            <Link href="/pilot">
              <a style={{ fontSize: 13, color: "#374151", textDecoration: "none", padding: "8px 12px", border: "1px solid #D1D5DB", borderRadius: 6, background: "#fff" }}>
                Back to home
              </a>
            </Link>
          </div>
        </div>

        {/* Progress */}
        <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: 16, marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
              <span style={{ color: "#374151" }}>Reviewed: <strong>{overall.reviewed}</strong> of {overall.total}</span>
              <span style={{ color: "#6B7280" }}>{overall.pct}%</span>
            </div>
            <div style={{ height: 6, background: "#E5E7EB", borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${overall.pct}%`, background: "#16A34A", transition: "width 0.3s" }} />
            </div>
          </div>
          <button
            onClick={resetAll}
            style={{ fontSize: 12, padding: "6px 10px", background: "#fff", color: "#991B1B", border: "1px solid #FCA5A5", borderRadius: 6, cursor: "pointer" }}
          >
            Reset all
          </button>
        </div>

        {/* Gate tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 16, flexWrap: "wrap" }}>
          {[1,2,3,4,5,6,7,8,9,10].map((cp) => {
            const c = gateCounts[cp];
            const isActive = activeGate === cp;
            const allReviewed = c.total > 0 && c.reviewed === c.total;
            return (
              <button
                key={cp}
                onClick={() => setActiveGate(cp)}
                style={{
                  padding: "10px 14px",
                  background: isActive ? "#111827" : "#fff",
                  color: isActive ? "#fff" : "#374151",
                  border: `1px solid ${isActive ? "#111827" : "#D1D5DB"}`,
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 500,
                  display: "flex", flexDirection: "column", alignItems: "flex-start",
                  minWidth: 120,
                }}
              >
                <span style={{ fontSize: 11, opacity: 0.8 }}>CP{cp}</span>
                <span>{GATE_NAMES[cp]}</span>
                <span style={{ fontSize: 10, marginTop: 2, opacity: 0.7 }}>
                  {c.reviewed}/{c.total}
                  {c.flagged > 0 && <span style={{ color: isActive ? "#FCD34D" : "#CA8A04" }}> · {c.flagged} flagged</span>}
                  {allReviewed && <span style={{ color: isActive ? "#86EFAC" : "#059669" }}> · done</span>}
                </span>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151", cursor: "pointer" }}>
            <input type="checkbox" checked={showOnlySuspicious} onChange={(e) => setShowOnlySuspicious(e.target.checked)} />
            Only flagged
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151", cursor: "pointer" }}>
            <input type="checkbox" checked={showOnlyUndecided} onChange={(e) => setShowOnlyUndecided(e.target.checked)} />
            Only undecided
          </label>
          <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: "auto" }}>
            Showing {visibleQuestions.length} of {(byGate[activeGate] ?? []).length}
          </span>
        </div>

        {loading && <div style={{ color: "#6B7280", fontSize: 14 }}>Loading question bank…</div>}

        {/* Questions list */}
        {!loading && visibleQuestions.map((q) => {
          const decision = state.decisions[q.key];
          const sus = isSuspicious(q);
          const isEditing = editingKey === q.key;
          const editedLabel = decision?.editedLabel ?? q.label;
          const editedHelp = decision?.editedHelpText ?? q.helpText ?? "";
          const note = decision?.note ?? "";

          const decisionColor =
            decision?.decision === "keep" ? "#065F46" :
            decision?.decision === "edit" ? "#92400E" :
            decision?.decision === "remove" ? "#991B1B" : "#9CA3AF";

          return (
            <div
              key={q.key}
              style={{
                background: "#fff",
                border: `1px solid ${sus.flagged ? "#FCD34D" : "#E5E7EB"}`,
                borderLeft: decision ? `4px solid ${decisionColor}` : `1px solid ${sus.flagged ? "#FCD34D" : "#E5E7EB"}`,
                borderRadius: 8,
                padding: 16,
                marginBottom: 10,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                    {q.key}
                  </div>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedLabel}
                      onChange={(e) => setDecision(q.key, "edit", { editedLabel: e.target.value, editedHelpText: editedHelp, note })}
                      style={{ width: "100%", fontSize: 15, fontWeight: 500, color: "#111827", padding: 6, border: "1px solid #D1D5DB", borderRadius: 4, marginBottom: 6 }}
                    />
                  ) : (
                    <div style={{ fontSize: 15, fontWeight: 500, color: "#111827", marginBottom: 4 }}>
                      {decision?.editedLabel ? (
                        <>
                          <span style={{ textDecoration: "line-through", color: "#9CA3AF" }}>{q.label}</span>
                          <span style={{ display: "block", color: "#92400E", marginTop: 2 }}>→ {decision.editedLabel}</span>
                        </>
                      ) : q.label}
                    </div>
                  )}
                  {q.helpText && !isEditing && (
                    <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 6 }}>{q.helpText}</div>
                  )}
                  {isEditing && (
                    <textarea
                      value={editedHelp}
                      onChange={(e) => setDecision(q.key, "edit", { editedLabel, editedHelpText: e.target.value, note })}
                      placeholder="Help text (shown under the question)"
                      rows={2}
                      style={{ width: "100%", fontSize: 13, padding: 6, border: "1px solid #D1D5DB", borderRadius: 4, marginBottom: 6, fontFamily: "inherit" }}
                    />
                  )}
                  {q.options && q.options.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 12, color: "#6B7280" }}>
                      {q.options.slice(0, 4).map((o) => (
                        <span key={o.key} style={{ padding: "2px 8px", background: "#F3F4F6", borderRadius: 4 }}>
                          {o.label}{o.points !== undefined ? ` · ${o.points}pt` : ""}
                        </span>
                      ))}
                      {q.options.length > 4 && (
                        <span style={{ padding: "2px 8px", color: "#9CA3AF" }}>+{q.options.length - 4} more</span>
                      )}
                    </div>
                  )}
                  {sus.flagged && (
                    <div style={{ marginTop: 8, padding: "6px 10px", background: "#FEF3C7", borderRadius: 4, fontSize: 12, color: "#92400E" }}>
                      Flagged: {sus.reasons.join(" · ")}
                    </div>
                  )}
                  {decision && (
                    <input
                      type="text"
                      value={note}
                      onChange={(e) => setDecision(q.key, decision.decision, { editedLabel: decision.editedLabel, editedHelpText: decision.editedHelpText, note: e.target.value })}
                      placeholder="Optional note (why we kept / edited / removed)"
                      style={{ width: "100%", marginTop: 8, fontSize: 12, padding: 6, border: "1px solid #E5E7EB", borderRadius: 4 }}
                    />
                  )}
                </div>

                {/* Action buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 100 }}>
                  <button
                    onClick={() => { setDecision(q.key, "keep"); setEditingKey(null); }}
                    style={{
                      padding: "6px 10px", fontSize: 12, fontWeight: 600,
                      background: decision?.decision === "keep" ? "#16A34A" : "#fff",
                      color: decision?.decision === "keep" ? "#fff" : "#065F46",
                      border: `1px solid ${decision?.decision === "keep" ? "#16A34A" : "#86EFAC"}`,
                      borderRadius: 4, cursor: "pointer",
                    }}
                  >
                    Keep
                  </button>
                  <button
                    onClick={() => { setDecision(q.key, "edit"); setEditingKey(q.key); }}
                    style={{
                      padding: "6px 10px", fontSize: 12, fontWeight: 600,
                      background: decision?.decision === "edit" ? "#EAB308" : "#fff",
                      color: decision?.decision === "edit" ? "#fff" : "#92400E",
                      border: `1px solid ${decision?.decision === "edit" ? "#EAB308" : "#FCD34D"}`,
                      borderRadius: 4, cursor: "pointer",
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => { setDecision(q.key, "remove"); setEditingKey(null); }}
                    style={{
                      padding: "6px 10px", fontSize: 12, fontWeight: 600,
                      background: decision?.decision === "remove" ? "#EF4444" : "#fff",
                      color: decision?.decision === "remove" ? "#fff" : "#991B1B",
                      border: `1px solid ${decision?.decision === "remove" ? "#EF4444" : "#FCA5A5"}`,
                      borderRadius: 4, cursor: "pointer",
                    }}
                  >
                    Remove
                  </button>
                  {decision && (
                    <button
                      onClick={() => { clearDecision(q.key); setEditingKey(null); }}
                      style={{ padding: "4px 8px", fontSize: 11, color: "#6B7280", background: "transparent", border: "none", cursor: "pointer", textDecoration: "underline" }}
                    >
                      Undo
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {!loading && visibleQuestions.length === 0 && (byGate[activeGate] ?? []).length > 0 && (
          <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, padding: 24, textAlign: "center", color: "#6B7280", fontSize: 14, marginBottom: 16 }}>
            No questions match the active filters. Toggle off "Only flagged" or "Only undecided" to see more.
          </div>
        )}

        {/* Add a question */}
        <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: 16, marginTop: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#065F46", marginBottom: 8 }}>
            Add a question to CP{activeGate} — {GATE_NAMES[activeGate]}
          </div>
          <input
            type="text"
            value={newQuestionLabel}
            onChange={(e) => setNewQuestionLabel(e.target.value)}
            placeholder="Question label (e.g., 'Has the buyer named the procurement contact?')"
            style={{ width: "100%", fontSize: 14, padding: 8, border: "1px solid #BBF7D0", borderRadius: 4, marginBottom: 8 }}
          />
          <textarea
            value={newQuestionHelp}
            onChange={(e) => setNewQuestionHelp(e.target.value)}
            placeholder="Help text (optional context shown to the AE)"
            rows={2}
            style={{ width: "100%", fontSize: 13, padding: 8, border: "1px solid #BBF7D0", borderRadius: 4, marginBottom: 8, fontFamily: "inherit" }}
          />
          <button
            onClick={addQuestion}
            disabled={!newQuestionLabel.trim()}
            style={{
              padding: "8px 16px", fontSize: 13, fontWeight: 600,
              background: newQuestionLabel.trim() ? "#065F46" : "#9CA3AF",
              color: "#fff", border: "none", borderRadius: 4,
              cursor: newQuestionLabel.trim() ? "pointer" : "default",
            }}
          >
            Add to CP{activeGate}
          </button>
        </div>

        {/* Added questions list for this gate */}
        {addedForGate.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, color: "#065F46", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
              Proposed additions for CP{activeGate} ({addedForGate.length})
            </div>
            {state.added.map((a, idx) => {
              if (a.cp !== activeGate) return null;
              return (
                <div key={idx} style={{ background: "#fff", border: "1px solid #BBF7D0", borderRadius: 6, padding: 12, marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "#111827" }}>{a.label}</div>
                    {a.helpText && <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{a.helpText}</div>}
                  </div>
                  <button
                    onClick={() => removeAdded(idx)}
                    style={{ fontSize: 11, color: "#991B1B", background: "transparent", border: "none", cursor: "pointer", textDecoration: "underline" }}
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </OperatorShell>
  );
}
